import { useState, useEffect, useRef } from 'react';

interface ComparisonRow {
  metric: string;
  icon: string;
  industries: {
    name: string;
    drone: string;
    traditional: string;
    advantage: string;
  }[];
}

const comparisonData: ComparisonRow[] = [
  {
    metric: 'Application Cost',
    icon: 'ri-money-dollar-circle-line',
    industries: [
      { name: 'Row Crop Farming', drone: '$18–22/ac', traditional: '$14–18/ac + $47/ac compaction damage', advantage: '$25/ac net savings' },
      { name: 'Grain Production', drone: '$20–24/ac', traditional: '$15–19/ac + $60–90/ac yield loss from delays', advantage: '$40–70/ac net savings' },
      { name: 'Vineyards & Orchards', drone: '$35–50/ac', traditional: '$80–120/ac (tractor + labor)', advantage: '50–60% cost reduction' },
      { name: 'Timber & Woodland', drone: '$12–18/ac', traditional: '$45–75/ac (ground crew)', advantage: '70% cost reduction' },
      { name: 'Livestock & Pasture', drone: '$15–20/ac', traditional: '$22–35/ac (ATV/tractor)', advantage: '30–45% cost reduction' },
      { name: 'Conservation & CRP', drone: '$14–18/ac', traditional: '$20–30/ac (drill/broadcast)', advantage: '35% cost reduction' },
    ],
  },
  {
    metric: 'Yield / Productivity Impact',
    icon: 'ri-bar-chart-grouped-line',
    industries: [
      { name: 'Row Crop Farming', drone: '+9.2 bu/ac avg. gain', traditional: '3–5% stand loss from wheel traffic', advantage: '$31/ac profit gain' },
      { name: 'Grain Production', drone: '+7.8–11.3 bu/ac gain', traditional: '5–12% loss from missed windows', advantage: '$42/ac annual profit increase' },
      { name: 'Vineyards & Orchards', drone: '95%+ canopy coverage', traditional: '60–75% canopy penetration', advantage: '20–30% better disease control' },
      { name: 'Timber & Woodland', drone: '85% invasive control rate', traditional: '40–55% control rate', advantage: '2x more effective treatment' },
      { name: 'Livestock & Pasture', drone: '70%+ weed reduction', traditional: '45–55% weed reduction', advantage: '25% better forage quality' },
      { name: 'Conservation & CRP', drone: '92% cover crop success', traditional: '65–75% establishment rate', advantage: '20%+ better stand establishment' },
    ],
  },
  {
    metric: 'Speed & Coverage',
    icon: 'ri-speed-line',
    industries: [
      { name: 'Row Crop Farming', drone: '200+ ac/day', traditional: '80–120 ac/day', advantage: '2x faster coverage' },
      { name: 'Grain Production', drone: '200 ac/day', traditional: '60–100 ac/day', advantage: '2–3x faster turnaround' },
      { name: 'Vineyards & Orchards', drone: '40–60 ac/day', traditional: '8–15 ac/day', advantage: '4x faster application' },
      { name: 'Timber & Woodland', drone: '500 ac/day (survey)', traditional: '20–40 ac/day (ground crew)', advantage: '12x faster surveys' },
      { name: 'Livestock & Pasture', drone: '150 ac/day', traditional: '40–60 ac/day', advantage: '3x faster coverage' },
      { name: 'Conservation & CRP', drone: '250 ac/day', traditional: '60–80 ac/day', advantage: '3–4x faster seeding' },
    ],
  },
  {
    metric: 'Soil & Crop Impact',
    icon: 'ri-leaf-line',
    industries: [
      { name: 'Row Crop Farming', drone: 'Zero compaction, zero stand loss', traditional: '6.4% yield loss in wheel tracks', advantage: 'No crop damage' },
      { name: 'Grain Production', drone: 'No field entry required', traditional: 'Ruts, lodging, broken stems', advantage: 'Preserves full stand' },
      { name: 'Vineyards & Orchards', drone: 'No root zone disturbance', traditional: 'Tractor compaction damages roots', advantage: '100% root protection' },
      { name: 'Timber & Woodland', drone: 'No trail cutting needed', traditional: 'Trail damage, erosion risk', advantage: 'Zero ground disturbance' },
      { name: 'Livestock & Pasture', drone: 'No soil disturbance', traditional: 'Ruts degrade grazing quality', advantage: 'Preserves pasture integrity' },
      { name: 'Conservation & CRP', drone: 'CRP-compliant, zero disturbance', traditional: 'Drill marks, soil disruption', advantage: '100% compliance maintained' },
    ],
  },
  {
    metric: 'Weather & Access Flexibility',
    icon: 'ri-cloud-line',
    industries: [
      { name: 'Row Crop Farming', drone: 'Flies 24–48 hrs after rain', traditional: 'Wait 3–7 days for field to dry', advantage: '3–5 day head start' },
      { name: 'Grain Production', drone: 'Applies during critical windows', traditional: 'Often misses optimal timing', advantage: '100% window accuracy' },
      { name: 'Vineyards & Orchards', drone: 'Navigates steep slopes easily', traditional: 'Limited on >15% grade terrain', advantage: 'Full terrain access' },
      { name: 'Timber & Woodland', drone: 'Reaches any canopy or terrain', traditional: 'Blocked by dense undergrowth', advantage: 'No access limitations' },
      { name: 'Livestock & Pasture', drone: 'Covers rough, hilly terrain', traditional: 'ATV limited on wet/steep ground', advantage: 'All-terrain capability' },
      { name: 'Conservation & CRP', drone: 'Applies on sensitive wetlands', traditional: 'Equipment banned in many areas', advantage: 'Access restricted areas' },
    ],
  },
];

const industryNames = [
  'Row Crop Farming',
  'Grain Production',
  'Vineyards & Orchards',
  'Timber & Woodland',
  'Livestock & Pasture',
  'Conservation & CRP',
];

const industryIcons: Record<string, string> = {
  'Row Crop Farming': 'ri-plant-line',
  'Grain Production': 'ri-seedling-line',
  'Vineyards & Orchards': 'ri-goblet-line',
  'Timber & Woodland': 'ri-tree-line',
  'Livestock & Pasture': 'ri-home-4-line',
  'Conservation & CRP': 'ri-earth-line',
};

// Total estimated annual savings per industry (per 1000 acres)
const industrySavings: Record<string, { savings: number; roi: number; timeReduction: number }> = {
  'Row Crop Farming': { savings: 25000, roi: 312, timeReduction: 50 },
  'Grain Production': { savings: 42000, roi: 285, timeReduction: 65 },
  'Vineyards & Orchards': { savings: 55000, roi: 420, timeReduction: 75 },
  'Timber & Woodland': { savings: 38000, roi: 380, timeReduction: 92 },
  'Livestock & Pasture': { savings: 18000, roi: 245, timeReduction: 67 },
  'Conservation & CRP': { savings: 15000, roi: 210, timeReduction: 72 },
};

function useAnimatedCounter(targetValue: number, duration: number = 1500) {
  const [count, setCount] = useState(0);
  const previousValue = useRef(0);

  useEffect(() => {
    const startValue = previousValue.current;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      
      const currentValue = Math.round(startValue + (targetValue - startValue) * easeOutQuart);
      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        previousValue.current = targetValue;
      }
    };

    requestAnimationFrame(animate);
  }, [targetValue, duration]);

  return count;
}

export default function ComparisonTable() {
  const [selectedIndustry, setSelectedIndustry] = useState(industryNames[0]);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const currentSavings = industrySavings[selectedIndustry];
  const animatedSavings = useAnimatedCounter(currentSavings.savings);
  const animatedRoi = useAnimatedCounter(currentSavings.roi);
  const animatedTime = useAnimatedCounter(currentSavings.timeReduction);

  const handleIndustryChange = (name: string) => {
    if (name !== selectedIndustry) {
      setIsTransitioning(true);
      setSelectedIndustry(name);
      setTimeout(() => setIsTransitioning(false), 300);
    }
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block text-emerald-700 font-bold text-xs tracking-widest uppercase mb-4">
            Side-by-Side Comparison
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Drone Spraying vs. Traditional Methods
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto font-light">
            Hard numbers across every industry we serve. See exactly where aerial application outperforms ground-based methods — and by how much.
          </p>
        </div>

        {/* Industry Selector Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {industryNames.map((name) => (
            <button
              key={name}
              onClick={() => handleIndustryChange(name)}
              className={`
                flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap
                ${selectedIndustry === name
                  ? 'bg-emerald-800 text-white shadow-md shadow-emerald-900/20'
                  : 'bg-stone-100 text-gray-600 hover:bg-stone-200 hover:text-gray-800'
                }
              `}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${industryIcons[name]} text-sm`}></i>
              </div>
              {name}
            </button>
          ))}
        </div>

        {/* Animated Savings Counter */}
        <div className={`mb-12 transition-all duration-300 ${isTransitioning ? 'opacity-50 scale-98' : 'opacity-100 scale-100'}`}>
          <div className="bg-gradient-to-br from-emerald-800 via-emerald-700 to-emerald-900 rounded-2xl p-8 md:p-10 shadow-xl shadow-emerald-900/20 relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
            </div>

            <div className="relative z-10">
              <div className="flex items-center justify-center gap-2 mb-6">
                <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
                  <i className={`${industryIcons[selectedIndustry]} text-white text-lg`}></i>
                </div>
                <h3 className="text-white/90 font-semibold text-lg">{selectedIndustry}</h3>
              </div>

              <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                {/* Annual Savings */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-2">
                    <span className="text-emerald-300 text-2xl md:text-3xl font-light">$</span>
                    <span className="text-white text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                      {animatedSavings.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-money-dollar-circle-line text-emerald-300"></i>
                    </div>
                    <span className="text-emerald-200 text-sm font-medium">Annual Savings / 1,000 ac</span>
                  </div>
                </div>

                {/* ROI Percentage */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-2">
                    <span className="text-white text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                      {animatedRoi}
                    </span>
                    <span className="text-emerald-300 text-2xl md:text-3xl font-light">%</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-line-chart-line text-emerald-300"></i>
                    </div>
                    <span className="text-emerald-200 text-sm font-medium">Average ROI</span>
                  </div>
                </div>

                {/* Time Reduction */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-2">
                    <span className="text-white text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                      {animatedTime}
                    </span>
                    <span className="text-emerald-300 text-2xl md:text-3xl font-light">%</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-timer-flash-line text-emerald-300"></i>
                    </div>
                    <span className="text-emerald-200 text-sm font-medium">Faster Application</span>
                  </div>
                </div>
              </div>

              {/* Animated Pulse Indicator */}
              <div className="flex items-center justify-center mt-6 gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400"></span>
                </span>
                <span className="text-emerald-300/80 text-xs font-medium">Live calculation based on industry averages</span>
              </div>
            </div>
          </div>
        </div>

        {/* Comparison Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {comparisonData.map((row) => {
            const industryData = row.industries.find(
              (ind) => ind.name === selectedIndustry
            );
            if (!industryData) return null;

            return (
              <div
                key={row.metric}
                className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                {/* Card Header */}
                <div className="px-6 pt-6 pb-4 border-b border-gray-50">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-lg">
                      <i className={`${row.icon} text-lg text-emerald-700`}></i>
                    </div>
                    <h3 className="font-bold text-gray-900 text-sm">{row.metric}</h3>
                  </div>
                </div>

                {/* Drone vs Traditional */}
                <div className="px-6 py-4 space-y-4">
                  {/* Drone */}
                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <span className="text-xs font-bold text-emerald-700 uppercase tracking-wide">Drone Application</span>
                    </div>
                    <p className="text-sm text-gray-800 font-medium pl-4">{industryData.drone}</p>
                  </div>

                  {/* Traditional */}
                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-2 h-2 rounded-full bg-red-400"></div>
                      <span className="text-xs font-bold text-red-500 uppercase tracking-wide">Traditional Method</span>
                    </div>
                    <p className="text-sm text-gray-800 font-medium pl-4">{industryData.traditional}</p>
                  </div>
                </div>

                {/* Advantage Footer */}
                <div className="px-6 py-3.5 bg-emerald-50 border-t border-emerald-100">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-up-circle-fill text-emerald-600 text-base"></i>
                    </div>
                    <span className="text-sm font-bold text-emerald-800">{industryData.advantage}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary Bar */}
        <div className="bg-stone-50 rounded-lg border border-gray-100 p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <div className="flex items-center gap-3 shrink-0">
              <div className="w-11 h-11 flex items-center justify-center bg-emerald-100 rounded-full">
                <i className="ri-trophy-line text-xl text-emerald-700"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm">Bottom Line for {selectedIndustry}</h4>
                <p className="text-xs text-gray-500">Across all metrics</p>
              </div>
            </div>

            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 w-full">
              {(() => {
                const summaryItems = [
                  { label: 'Cost', icon: 'ri-money-dollar-circle-line' },
                  { label: 'Yield', icon: 'ri-bar-chart-grouped-line' },
                  { label: 'Speed', icon: 'ri-speed-line' },
                  { label: 'Impact', icon: 'ri-leaf-line' },
                ];
                return summaryItems.map((item, i) => {
                  const row = comparisonData[i];
                  const data = row?.industries.find((ind) => ind.name === selectedIndustry);
                  return (
                    <div key={item.label} className="text-center">
                      <div className="w-7 h-7 flex items-center justify-center mx-auto mb-1.5">
                        <i className={`${item.icon} text-emerald-600 text-lg`}></i>
                      </div>
                      <div className="text-xs font-bold text-emerald-800 mb-0.5">{data?.advantage || '—'}</div>
                      <div className="text-[11px] text-gray-500">{item.label}</div>
                    </div>
                  );
                });
              })()}
            </div>

            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-emerald-800 hover:bg-emerald-700 text-white font-bold px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap shrink-0 text-sm"
            >
              Get a Custom Quote
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
